// lib/api-client.ts (Vite: uses api-config getApiUrl)

import { getApiBaseUrl, getApiUrl } from './api-config';
import { checkAuthError } from './api-utils';
import { getAuthToken, handleAuthError } from './auth-client';
import { getPendingLicenseKey } from './license-utils';

type ApiFetchOptions = RequestInit & { _skipAuth?: boolean };

// Helper function for fetch with credentials and proper headers
async function apiFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { _skipAuth, ...fetchOptions } = options;

  // Normalize headers into a concrete Headers instance so we can safely mutate it
  const headers = new Headers({
    Accept: 'application/json',
  });

  const originalHeaders = fetchOptions.headers;
  if (originalHeaders) {
    if (originalHeaders instanceof Headers) {
      originalHeaders.forEach((value, key) => {
        headers.append(key, value);
      });
    } else if (Array.isArray(originalHeaders)) {
      for (const [key, value] of originalHeaders) {
        if (value !== undefined) {
          headers.append(key, String(value));
        }
      }
    } else {
      Object.entries(originalHeaders).forEach(([key, value]) => {
        if (value !== undefined) {
          headers.append(key, String(value));
        }
      });
    }
  }

  // Only set Content-Type for non-FormData requests when it's not already provided
  if (!(fetchOptions.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Attach bearer token automatically when available and not already provided (skip for auth-acquisition)
  if (!_skipAuth && !headers.has('Authorization')) {
    try {
      const token = getAuthToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // Ignore token lookup errors in non-browser environments
    }
  }

  const res = await fetch(url, {
    ...fetchOptions,
    credentials: 'include',
    headers,
  });

  // On 401 from authenticated requests, handle session revocation / logout
  if (res.status === 401 && headers.has('Authorization')) {
    const handled = await handleAuthError(res);
    if (handled) return res;
  }

  return res;
}

/* ========= Types (match your backend response) ========= */

export type TrendingTool = {
    id: string;
    slug: string;
    coverImage: string | null;
    logoUrl: string | null;
    name: string;
    shortDesc: string;
    avgRating: number;
    reviewCount: number;
    featuredTag: string | null;
};

export type HomeCategory = {
    id: string;
    slug: string;
    iconUrl: string | null;
    name: string;
    shortDesc: string;
};

export type HomeNewsItem = {
    id: string;
    slug: string;
    coverImage: string | null;
    title: string;
    excerpt: string;
    viewsCount: number;
    category?: string | null;
    type?: string | null;
    url?: string | null;
    tag?: string | null;
    publishedAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type HomeEventTab = {
    id: string;
    code: string;
    label: string;
};

export type HomeEventItem = {
    id: string;
    slug: string;
    coverImage: string | null;
    title: string;
    descriptionHtml: string;
    startsAt: string;
    endsAt: string | null;
    location: string | null;
    isFeatured: boolean;
    url: string | null;
};

export type HomeEventsBlock = {
    tabs: HomeEventTab[];
    items: HomeEventItem[];
};

export type HomeTestimonial = {
    id: string;
    name: string;
    message: string;
    profileImage: string | null;
    userRole: string | null;
    stars: number;
};

export type HomePlanFeature = {
    id: string;
    code: string;
    label: string;
    included: boolean;
    value: string | null;
};

export type SeatPricingTier = {
    minSeats: number;
    maxSeats: number | null;
    currency: string;
    pricePerSeatMonthlyCents: number;
    pricePerSeatYearlyCents: number;
};

export type HomePlan = {
    id: string;
    code: string;
    tier: string | null;
    currency: string;
    priceMonthlyCents: number | null;
    priceYearlyCents: number | null;
    priceMonthlyOriginalCents: number | null;
    priceYearlyOriginalCents: number | null;
    monthlySavingsCents: number | null;
    yearlySavingsCents: number | null;
    yearlySavingsPercent: number | null;
    yearlyEffectiveMonthlyCents: number | null;
    showPrice: boolean;
    ctaType: 'LOGIN' | 'COMING SOON' | 'CONTACT';
    ctaLabel: string | null;
    ctaUrl: string | null;
    name: string;
    subtitle: string;
    showOnHome: boolean;
    showOnPricing: boolean;
    sortOrder: number | null;
    features: HomePlanFeature[];
    // Corporate pricing calculator (optional)
    seatPricingTiers?: SeatPricingTier[];
    // Legacy fields for backward compatibility
    amountCents?: number;
    interval?: string;
};

export type HomeBlog = {
    id: string;
    slug: string;
    coverImage: string | null;
    title: string;
    excerpt: string;
    viewsCount: number;
    likesCount: number;
    commentsCount: number;
};

export type HomeFAQItem = {
    id: string;
    slug: string;
    question: string;
    answer: string;
    order?: number;
};

export type University = {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryDomain: string;
  redirect_url: string;
};

export type LegalPageMeta = {
    id: string;
    slug: string;
    title: string;
    updatedAt?: string;
};

export type LegalPage = {
    id: string;
    slug: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    contentHtml: string;
};

export type PricingFaqItem = {
    id: string;
    question: string;
    answer: string;
    sortOrder?: number;
};

export type HomePageData = {
    trendingTools: TrendingTool[];
    categories: HomeCategory[];
    news: HomeNewsItem[];
    events: HomeEventsBlock;
    testimonials: HomeTestimonial[];
    plans: HomePlan[];
    blogs: HomeBlog[];
    faqs?: HomeFAQItem[];
    universities?: University[];
};

/* ========= Fetch helper ========= */

export async function fetchHomePageData(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<HomePageData> {
    const url = getApiUrl(`/home-page-data?lang=${encodeURIComponent(lang)}`);

    const res = await apiFetch(url, {
        method: 'GET',
        signal,
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load home page data (${res.status})`;
        throw new Error(message);
    }

    const json = (await res.json()) as HomePageData;
    return json;
}

/* ========= Legal pages ========= */

export async function fetchLegalPages(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<LegalPageMeta[]> {
    const url = getApiUrl(`/legal/pages?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load legal pages (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as LegalPageMeta[];
}

export async function fetchLegalPageBySlug(
    slug: string,
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<LegalPage> {

    const url = getApiUrl(`/legal/pages/${slug}?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load legal page (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as LegalPage;
}

/* ========= Pricing ========= */

export async function fetchPricingPlans(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<HomePlan[]> {
    const url = getApiUrl(`/plans?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load plans (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as HomePlan[];
}

export type SeatPricingResponse = {
    planId: string;
    currency: string;
    tiers: SeatPricingTier[];
};

export async function fetchSeatPricing(
    planId: string,
    signal?: AbortSignal,
): Promise<SeatPricingResponse> {
    const url = getApiUrl(`/plans/${encodeURIComponent(planId)}/seat-pricing`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        if (res.status === 404) {
            // Plan not found - return empty tiers
            return {
                planId,
                currency: 'CHF',
                tiers: [],
            };
        }
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load seat pricing (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as SeatPricingResponse;
}

export async function fetchPricingFaqs(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<PricingFaqItem[]> {
    const url = getApiUrl(`/legal/faqs?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load pricing FAQs (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as PricingFaqItem[];
}

export type MyLicenseResponse = {
    licenseKey: string;
    status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'FROZEN';
    flow: 'INDIVIDUAL' | 'CORPORATE';
    seatsPurchased: number;
    seatsUsed: number;
    endsAt: string | null;
    manageUrl: string | null;
    planId?: string | null;
    cancellationUrl?: string | null;
    changePaymentSubscriptionUrl?: string | null;
    customerPdfDocumentUrl?: string | null;
    provider?: 'STRIPE' | 'CLEVERBRIDGE' | null;
};

export async function getMyLicense(token: string): Promise<MyLicenseResponse | null> {
    const res = await apiFetch(getApiUrl('/licenses/my'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (res.status === 404 || res.status === 204) {
        return null;
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load license');
    }

    return (await res.json()) as MyLicenseResponse;
}

/* ========= Onboarding / Questionnaire ========= */

function getRootApiUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const base = getApiBaseUrl().replace(/\/api$/i, '');
    return `${base}${cleanPath}`;
}

export type OnboardingQuestion = {
    id: string;
    text: string;
    options: string[];
};

export type OnboardingAnswer = {
    questionId: string;
    selectedIndex: number;
};

export type SubmitOnboardingRequest = {
    goal: string;
    role: string;
    aiLevel: 'Beginner' | 'Intermediate' | 'Advanced';
    acquisitionSource: string;
    answers: OnboardingAnswer[];
};

export type SubmitOnboardingResponse = {
    score: number;
    level: string;
    strengths: string[];
    weaknesses: string[];
    recommendedLevel: number;
};

export type OnboardingStatusResponse = {
    completed: boolean;
    onboarding: Record<string, unknown> | null;
    skillScore: Record<string, unknown> | null;
    score?: number;
    level?: string;
};

export async function fetchOnboardingQuestions(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<OnboardingQuestion[]> {
    const url = getRootApiUrl(`/onboarding/questions?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { method: 'GET', signal });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Failed to load questionnaire (${res.status})`);
    }
    const raw = await res.json();
    const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return data.map((item: any) => ({
        id: String(item.id || ''),
        text: String(item.text || ''),
        options: Array.isArray(item.options) ? item.options.map((option: unknown) => String(option)) : [],
    })).filter((item: OnboardingQuestion) => item.id && item.text && item.options.length > 0);
}

export async function getOnboardingStatus(token?: string | null): Promise<OnboardingStatusResponse> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await apiFetch(getRootApiUrl('/onboarding/status'), {
        method: 'GET',
        headers,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Failed to load onboarding status (${res.status})`);
    }
    const raw = await res.json();
    const data = raw?.data && typeof raw.data === 'object' ? raw.data : raw;
    return {
        ...data,
        completed: data?.completed === true,
        onboarding: data?.onboarding || null,
        skillScore: data?.skillScore || null,
    };
}

export async function submitOnboarding(
    payload: SubmitOnboardingRequest,
    token?: string | null,
): Promise<SubmitOnboardingResponse> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await apiFetch(getRootApiUrl('/onboarding/submit'), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = Array.isArray(body?.message) ? body.message[0] : body?.message;
        throw new Error(message || `Failed to submit questionnaire (${res.status})`);
    }
    const raw = await res.json();
    return (raw?.data && typeof raw.data === 'object' ? raw.data : raw) as SubmitOnboardingResponse;
}

/* ========= News ========= */

export type NewsItem = {
    id: string;
    slug: string;
    url: string | null;
    coverImage: string | null;
    title: string | null;
    summary: string | null;
    viewsCount: number;
    likesCount: number;
    commentsCount: number;
    publishedAt: string | null;
    category: string | null;
    excerpt: string | null;
};

export type PaginationMeta = {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
};

/* ========= Blogs ========= */

export type BlogItem = {
    id: string;
    slug: string;
    coverImage: string | null;
    publishedAt: string;
    title: string;
    excerpt: string;
    viewsCount: number;
    likesCount: number;
    commentsCount: number;
};

export type BlogDetail = {
    id: string;
    slug: string;
    coverImage: string | null;
    publishedAt: string;
    createdAt: string;
    updatedAt: string;
    title: string;
    excerpt: string;
    contentHtml: string;
    viewsCount: number;
    likesCount: number;
    commentsCount: number;
};

export type BlogResponse = {
    items: BlogItem[];
    meta: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export type NewsResponse = {
    items: NewsItem[];
    meta: PaginationMeta;
};

export async function fetchNews(
    lang: string = 'en',
    page: number = 1,
    pageSize: number = 12,
    signal?: AbortSignal,
): Promise<NewsResponse> {
    const url = getApiUrl(`/news?lang=${encodeURIComponent(lang)}&page=${page}&pageSize=${pageSize}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load news (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as NewsResponse;
}

export async function fetchBlogs(
    lang: string = 'en',
    page: number = 1,
    pageSize: number = 10,
    signal?: AbortSignal,
): Promise<BlogResponse> {
    const url = getApiUrl(`/blogs?lang=${encodeURIComponent(lang)}&page=${page}&pageSize=${pageSize}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load blogs (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as BlogResponse;
}

export async function fetchBlogBySlug(
    slug: string,
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<BlogDetail> {
    const url = getApiUrl(`/blogs/${slug}?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load blog (${res.status})`;
        throw new Error(message);
    }

    try {
        const data = (await res.json()) as Record<string, unknown>;
        const contentHtml =
            (data.contentHtml as string) ??
            (data.content_html as string) ??
            (data.content as string) ??
            '';
        return { ...data, contentHtml } as BlogDetail;
    } catch (e) {
        if (e instanceof SyntaxError) {
            throw new Error('Blog API returned empty or invalid response');
        }
        throw e;
    }
}

/* ========= Tools ========= */

export type ToolItem = {
    id: string;
    slug: string;
    name: string;
    shortDesc?: string | null;
    // New API fields
    thumbnailUrl?: string | null;
    avgRating?: number | null;
    likes?: number | null;
    platformSupport?: string | null;
    videoUrl?: string | null;
    // Legacy/optional fields kept for compatibility
    coverImage?: string | null;
    logoUrl?: string | null;
    categoryId?: string | null;
    categoryName?: string | null;
    reviewCount?: number | null;
    featuredTag?: string | null;
    url?: string | null;
    toolUrl?: string | null;
};

export type ToolsResponse = {
    items: ToolItem[];
    meta: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export async function fetchTools(
    lang: string = 'en',
    page: number = 1,
    pageSize: number = 12,
    q: string | null = null,
    categoryId: string | null = null,
    signal?: AbortSignal,
): Promise<ToolsResponse> {

    const params = new URLSearchParams({
        lang: lang,
        page: String(page),
        pageSize: String(pageSize),
    });

    // Search query: only send when non-empty
    if (q != null && q !== 'null' && q.trim()) {
        params.set('q', q.trim());
    }

    // Category: only send when a concrete category is selected
    if (categoryId && categoryId !== 'null') {
        params.set('categoryId', categoryId);
    }

    const url = getApiUrl(`/tools?${params.toString()}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load tools (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as ToolsResponse;
}

export type ToolCategory = {
    id: string;
    slug: string;
    name: string;
    description?: string | null;
    icon?: string | null;
    sortOrder?: number | null;
};

export type ToolCategoriesResponse = {
    items: ToolCategory[];
};

export async function fetchToolCategories(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<ToolCategory[]> {
    const url = getApiUrl(`/tools/categories?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load tool categories (${res.status})`;
        throw new Error(message);
    }

    const json = (await res.json()) as ToolCategoriesResponse;
    const items = json.items ?? [];

    // Sort by sortOrder, then name
    return items.sort((a, b) => {
        const orderDiff = (a.sortOrder || 0) - (b.sortOrder || 0);
        if (orderDiff !== 0) return orderDiff;
        return a.name.localeCompare(b.name);
    });
}

export type ToolDetail = {
    id: string;
    slug: string;
    name: string;
    shortDesc: string | null;
    overviewHtml: string | null;
    whyUseHtml: string | null;
    providerInfoHtml: string | null;
    pros: string[];
    cons: string[];
    useCases: string[];
    platformSupportHtml: string | null;
    avgRating: number;
    likes: number;
    platformSupport: string | null;
    toolUrl: string | null;
    createdAt: string;
    updatedAt: string;
    logoUrl: string | null;
    thumbnailUrl: string | null;
    videoUrl: string | null;
    category: {
        id: string;
        slug: string;
        name: string;
    } | null;
    course?: {
        id?: string;
        slug: string;
        level?: number;
        title?: string;
        thumbnailUrl?: string | null;
        isFree?: boolean;
    } | null;
    courses?: Array<{
        id?: string;
        slug: string;
        level?: number;
        title?: string;
        thumbnailUrl?: string | null;
        isFree?: boolean;
    }>;
};

export type ToolPricingPlan = {
    id: string;
    key: string;
    orderIndex: number;
    isMostPopular: boolean;
    billingType: string;
    trialPeriodDays: number | null;
    contactSales: boolean;
    title: string;
    description: string;
    ctaLabel: string | null;
};

export type ToolPricing = {
    id: string;
    isActive: boolean;
    showOnToolPage: boolean;
    meta: {
        pricingPage: string | null;
    };
    plans: ToolPricingPlan[];
};

export type ToolReview = {
    id: string;
    userId: string;
    userName: string;
    rating: number;
    comment: string | null;
    createdAt: string;
    helpfulCount: number;
};

export type ToolReviewsSummary = {
    totalReviews: number;
    avgRating: number;
    breakdown: {
        '1': number;
        '2': number;
        '3': number;
        '4': number;
        '5': number;
    };
};

export type ToolQA = {
    id: string;
    question: string;
    answer: string | null;
    createdAt: string;
};

export type ToolRelease = {
    id: string;
    version: string;
    releaseDate: string;
    features: string[];
};

export type ToolAlternative = {
    id: string;
    slug: string;
    name: string;
    shortDesc: string;
    avgRating: number;
    likes: number;
    logoUrl: string | null;
    thumbnailUrl: string | null;
};

export type ToolDetailResponse = {
    tool: ToolDetail;
    pricing: ToolPricing | null;
    reviewsSummary: ToolReviewsSummary;
    reviews: ToolReview[];
    qa: ToolQA[];
    releases: ToolRelease[];
    alternatives: ToolAlternative[];
    trendingTools: Array<{
        id: string;
        slug: string;
        name: string;
        shortDesc: string;
        avgRating: number;
        likes: number;
        logoUrl: string | null;
        thumbnailUrl: string | null;
    }>;
};

export async function fetchToolBySlug(
    slug: string,
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<ToolDetailResponse> {
    const url = getApiUrl(`/tools/slug/${slug}?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load tool (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as ToolDetailResponse;
}

/* ========= Contact ========= */

export type ContactInquiryRequest = {
    first_name: string;
    last_name: string;
    email: string;
    country_code: string;
    phone_number: string;
    submission_type?: string | null;
    sms_consent?: boolean;
    sms_consent_text?: string | null;
    sms_consent_at?: string | null;
    transactional_sms_consent?: boolean;
    transactional_sms_consent_text?: string | null;
    transactional_sms_consent_at?: string | null;
    promotional_sms_consent?: boolean;
    promotional_sms_consent_text?: string | null;
    promotional_sms_consent_at?: string | null;
    terms_accepted?: boolean;
    terms_accepted_at?: string | null;
    inquiry_category: string;
    event_category?: string | null;
    message?: string | null;
    company_name?: string | null;
    website?: string | null;
    commercial_number?: string | null;
    industry?: string | null;
    product_name?: string | null;
    product_url?: string | null;
    tool_category?: string | null;
    monetisation_model?: string | null;
    value_proposition?: string | null;
    objective?: string | null;
    budget_range?: string | null;
    activation_goals?: string | null;
    publication?: string | null;
    deadline?: string | null;
    coverage_type?: string | null;
    event_name?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    venue_information?: string | null;
    event_url?: string | null;
    event_category_list?: string | null;
    event_description?: string | null;
    issue?: string | null;
    details?: string | null;
    // University fields
    university_name?: string | null;
    university_domain?: string | null;
    university_admin_name?: string | null;
    university_admin_email?: string | null;
    university_admin_phone?: string | null;
    university_size?: string | null;
    university_country?: string | null;
    university_notes?: string | null;
};

export type ContactInquiryResponse = {
    id: string;
    createdAt: string;
};

export async function createContactInquiry(
    payload: ContactInquiryRequest,
    signal?: AbortSignal,
): Promise<ContactInquiryResponse> {
    const url = getApiUrl('/contact/inquiries');
    const res = await apiFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal,
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to submit contact inquiry (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as ContactInquiryResponse;
}

/* ========= Events ========= */

export type EventItem = {
    id: string;
    slug: string;
    url: string | null;
    coverImage: string | null;
    startsAt: string | null;
    endsAt: string | null;
    location: string | null;
    isOnline: boolean;
    isFeatured: boolean;
    typeCode: string | null;
    typeLabel: string | null;
    title: string;
    descriptionHtml: string;
};

export type EventsResponse = {
    items: EventItem[];
    meta: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export type EventTypeFilter = {
    id: string;
    code: string;
    label: string;
    showOnHomepage?: boolean;
    sortOrder?: number;
};

export async function fetchEvents(
    lang: string = 'en',
    page: number = 1,
    pageSize: number = 12,
    typeCode?: string,
    sort?: 'recent' | 'starting_soon' | 'past',
    signal?: AbortSignal,
): Promise<EventsResponse> {

    let path = `/events?lang=${encodeURIComponent(lang)}&page=${page}&pageSize=${pageSize}`;
    if (typeCode && typeCode !== 'all') {
        path += `&type=${encodeURIComponent(typeCode)}`;
    }
    if (sort) {
        path += `&sort=${encodeURIComponent(sort)}`;
    }
    const url = getApiUrl(path);

    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to load events (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as EventsResponse;
}

export async function fetchEventTypes(
    lang: string = 'en',
    signal?: AbortSignal,
): Promise<EventTypeFilter[]> {
    const url = getApiUrl(`/events/types?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, { signal });

    if (!res.ok) {
        // Return default tabs if endpoint doesn't exist
        return [
            { id: 'all', code: 'all', label: 'View All' },
            { id: 'webinar', code: 'WEBINAR', label: 'Webinars' },
            { id: 'workshop', code: 'WORKSHOP', label: 'Workshops' },
            { id: 'launch', code: 'LAUNCH', label: 'Product Launches' },
        ];
    }

    const data = (await res.json()) as EventTypeFilter[];
    // Sort by sortOrder if available
    return data.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

/* ========= Waitlist ========= */

export async function joinCompanyDashboardInterest(input: {
    name: string;
    email: string;
    phoneNumber: string;
    locale: 'en' | 'de';
    emailUpdatesConsent: boolean;
}): Promise<{ ok: boolean; id: string }> {
    const res = await apiFetch(getApiUrl('/waitlist/company-dashboard'), {
        method: 'POST',
        body: JSON.stringify(input),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Failed to join company dashboard list (${res.status})`);
    }

    return (await res.json()) as { ok: boolean; id: string };
}

export type WaitlistResponse = {
    ok: boolean;
    alreadyExists: boolean;
    id?: string;
};

export async function joinWaitlist(
    email: string,
    fullName: string,
    source: string = 'website',
): Promise<WaitlistResponse> {

    const res = await apiFetch(getApiUrl('/waitlist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, fullName, source }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to join waitlist (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as WaitlistResponse;
}

/* ========= Newsletter ========= */

export type NewsletterSubscribeResponse = {
    ok: boolean;
    id: string;
};

export async function subscribeNewsletter(
    email: string,
    language: string = 'en',
): Promise<NewsletterSubscribeResponse> {

    const res = await apiFetch(getApiUrl('/newsletter/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
            (body && (body.message as string)) ||
            `Failed to subscribe to newsletter (${res.status})`;
        throw new Error(message);
    }

    return (await res.json()) as NewsletterSubscribeResponse;
}

/* ========= Admin Auth ========= */

export type AdminUser = {
    id: string;
    email: string;
    name: string;
    role: string;
    sessionId?: string;
};

export type AdminLoginResponse = {
    ok: boolean;
    token: string;
    admin: AdminUser;
    expiresAt: string;
};

export type AdminMeResponse = {
    ok: boolean;
    admin: AdminUser;
};

export async function adminLogin(
    email: string,
    password: string,
): Promise<AdminLoginResponse> {

    const res = await apiFetch(getApiUrl('/admin/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 401) {
            throw new Error(body?.message || 'Invalid credentials');
        }
        throw new Error(body?.message || `Login failed (${res.status})`);
    }

    return (await res.json()) as AdminLoginResponse;
}

export async function getAdminMe(token: string): Promise<AdminMeResponse> {

    const res = await apiFetch(getApiUrl('/admin/me'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    // Check for authentication errors (403/Forbidden)
    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Unauthorized');
    }

    return (await res.json()) as AdminMeResponse;
}

/* ========= Community ========= */

export type CommunityRoom = {
    id: string;
    slug: string;
    title: string;
    description: string;
    ispublic: boolean;
    createdAt: string;
};

export type CommunityPostAuthor = {
    id: string;
    name: string;
    email: string;
};

export type CommunityPostAttachment = {
    url: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
};

export type CommunityPost = {
    id: string;
    content: string;
    linkUrl: string | null;
    linkTitle: string | null;
    linkDescription: string | null;
    linkImageUrl: string | null;
    isPinned: boolean;
    createdAt: string;
    author: CommunityPostAuthor;
    repliesCount: number;
    likesCount: number;
    likedByMe: boolean;
    attachments: CommunityPostAttachment[];
};

export type CommunityPostsResponse = {
    room: {
        id: string;
        slug: string;
        title: string;
    };
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    items: CommunityPost[];
};

export type CreatePostRequest = {
    roomId: string;
    content: string;
    linkUrl?: string;
    linkTitle?: string;
    linkDescription?: string;
    linkImageUrl?: string;
    attachments?: CommunityPostAttachment[];
};

export type CreatePostResponse = {
    id: string;
};

export async function uploadCommunityAttachment(
    token: string,
    file: File,
): Promise<CommunityPostAttachment> {

    const formData = new FormData();
    formData.append('file', file);

    const res = await apiFetch(getApiUrl('/dashboard/community/upload'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            // NOTE: do NOT set Content-Type, browser will set multipart boundary
        },
        body: formData,
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to upload attachment');
    }

    // The API returns the same shape as CommunityPostAttachment
    return (await res.json()) as CommunityPostAttachment;
}

export type CreateReplyRequest = {
    postId: string;
    content: string;
    parentReplyId?: string | null;
};

export type CreateReplyResponse = {
    id: string;
};

export type LikeResponse = {
    ok: boolean;
};

export async function getCommunityRooms(token: string): Promise<CommunityRoom[]> {

    const res = await apiFetch(getApiUrl('/dashboard/community/rooms'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load community rooms');
    }

    return (await res.json()) as CommunityRoom[];
}

export async function getRoomPosts(
    token: string,
    roomSlug: string,
    page: number = 1,
    pageSize: number = 20,
): Promise<CommunityPostsResponse> {
    const url = getApiUrl(`/dashboard/community/rooms/${roomSlug}/posts?page=${page}&pageSize=${pageSize}`);
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load room posts');
    }

    return (await res.json()) as CommunityPostsResponse;
}

export async function createPost(
    token: string,
    data: CreatePostRequest,
): Promise<CreatePostResponse> {

    const res = await apiFetch(getApiUrl('/dashboard/community/posts'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to create post');
    }

    return (await res.json()) as CreatePostResponse;
}

export async function createReply(
    token: string,
    data: CreateReplyRequest,
): Promise<CreateReplyResponse> {

    const res = await apiFetch(getApiUrl('/dashboard/community/replies'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to create reply');
    }

    return (await res.json()) as CreateReplyResponse;
}

export async function likePost(
    token: string,
    postId: string,
): Promise<LikeResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/community/posts/${postId}/like`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to like post');
    }

    return (await res.json()) as LikeResponse;
}

export async function unlikePost(
    token: string,
    postId: string,
): Promise<LikeResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/community/posts/${postId}/unlike`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to unlike post');
    }

    return (await res.json()) as LikeResponse;
}

export type PostReply = {
    id: string;
    userId: string;
    userName: string;
    content: string;
    createdAt: string;
    isOwner: boolean;
};

export type PostRepliesResponse = {
    postId: string;
    replies: PostReply[];
};

export async function getPostReplies(
    token: string,
    postId: string,
): Promise<PostRepliesResponse> {

    const res = await apiFetch(getApiUrl(`/community/posts/${postId}/replies`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load post replies');
    }

    return (await res.json()) as PostRepliesResponse;
}

/* ========= Notifications ========= */

export type Notification = {
    id: string;
    type: 'like' | 'reply' | 'mention' | 'course' | 'webinar' | 'badge' | string;
    postId: string | null;
    replyId: string | null;
    roomId: string | null;
    message: string;
    readAt: string | null;
    createdAt: string;
    actorUserId: string;
    actorName: string;
    actorEmail: string;
};

export type NotificationsResponse = {
    unreadCount?: number;
    items: Notification[];
    meta?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export type ReadNotificationResponse = {
    ok: boolean;
};

export async function getNotifications(
    token: string,
    unreadOnly: boolean = false,
    limit: number = 20,
): Promise<NotificationsResponse> {

    const params = new URLSearchParams();
    if (unreadOnly) {
        params.set('unreadOnly', 'true');
    }
    params.set('limit', String(limit));

    const url = getApiUrl(`/dashboard/community/notifications?${params.toString()}`);
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load notifications');
    }

    const data = await res.json();
    
    // Handle both array response and object response
    if (Array.isArray(data)) {
        return {
            items: data,
            unreadCount: data.filter((n) => !n.readAt).length,
        };
    }

    return data as NotificationsResponse;
}

export async function readNotification(
    token: string,
    notificationId: string,
): Promise<ReadNotificationResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/community/notifications/${notificationId}/read`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to mark notification as read');
    }

    return (await res.json()) as ReadNotificationResponse;
}

export async function readAllNotifications(
    token: string,
): Promise<ReadNotificationResponse> {

    const res = await apiFetch(getApiUrl('/notifications/read-all'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to mark all notifications as read');
    }

    return (await res.json()) as ReadNotificationResponse;
}

/* ========= Rewards ========= */

export type RewardsSummary = {
    totalPoints: number;
    lifetimePoints: number;
    unlockedPrompts: number;
};

export type RewardHistoryItem = {
    id: string;
    userId: string;
    type: string;
    amount: number;
    reason: string;
    meta: {
        score?: number;
        quizId?: string;
        courseId?: string;
        [key: string]: any;
    };
    createdAt: string;
    promptId: string | null;
};

export type RewardHistoryResponse = {
    items: RewardHistoryItem[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
};

export type UnlockedPrompt = {
    promptId: string;
    title: string;
    category: string;
    sortOrder: number;
    content: string;
    source: string | null;
    unlockedAt: string;
};

export type UnlockedPromptsResponse = {
    items: UnlockedPrompt[];
};

export async function getRewardsSummary(token: string): Promise<RewardsSummary> {

    const res = await apiFetch(getApiUrl('/dashboard/rewards/summary'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    // Check for authentication errors (403/Forbidden)
    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load rewards summary');
    }

    return (await res.json()) as RewardsSummary;
}

export async function getRewardsHistory(
    token: string,
    page: number = 1,
    pageSize: number = 20,
    type?: string,
): Promise<RewardHistoryResponse> {

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (type) {
        params.set('type', type);
    }

    const url = getApiUrl(`/dashboard/rewards/history?${params.toString()}`);
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load rewards history');
    }

    return (await res.json()) as RewardHistoryResponse;
}

export async function getUnlockedPrompts(token: string, lang?: string): Promise<UnlockedPromptsResponse> {

    const url = lang 
        ? getApiUrl(`/dashboard/rewards/prompts?lang=${encodeURIComponent(lang)}`)
        : getApiUrl('/dashboard/rewards/prompts');

    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load unlocked prompts');
    }

    return (await res.json()) as UnlockedPromptsResponse;
}

/* ========= Certificates ========= */

export type Certificate = {
    id: string;
    type: string;
    // Legacy fields (course-based certificates)
    title?: string;
    courseId?: string;
    verifyUrl?: string | null;
    course?: {
        id: string;
        slug: string;
        level: number | null;
        title: string;
    } | null;

    // Level-based certificate fields
    level: number | null;
    levelTitle?: string | null;
    certificateName?: string | null;
    certificateCode?: string | null;
    fileUrl: string;
    issuedAt: string;
    shareSlug?: string | null;
    shareUrl?: string | null;
    linkedinShareUrl?: string | null;
    issuingOrganization?: string | null;
    credentialUrl?: string | null;
    credential?: {
        name: string;
        organization: string;
        credentialId: string;
        credentialUrl: string;
    } | null;
};

/**
 * Returns true when the current user agent is likely a mobile device.
 */
export function isProbablyMobile(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod/i.test(ua);
}

/**
 * Builds the public certificate verification path for sharing.
 * Legacy behaviour: verification page lives at `/certificates?id=...`.
 * Preference order: certificateCode -> shareSlug -> id.
 * Returns an internal path (no origin).
 */
export function buildPublicCertificatePath(certificate: Certificate): string {
    const identifier =
        certificate.certificateCode ||
        certificate.shareSlug ||
        certificate.id;

    const safeId = encodeURIComponent(identifier);
    return `/certificates/?id=${safeId}`;
}

/**
 * Builds a full public certificate URL including origin, for sharing.
 * Uses the legacy `/certificates?id=...` URL so existing links keep working.
 */
export function buildPublicCertificateUrl(certificate: Certificate): string {
    const identifier =
        certificate.certificateCode ||
        certificate.shareSlug ||
        certificate.id;

    const safeId = encodeURIComponent(identifier);

    if (typeof window === 'undefined') {
        // SSR-safe fallback – caller can adjust to add origin later
        return `/certificates/?id=${safeId}`;
    }

    return `${window.location.origin}/certificates/?id=${safeId}`;
}

/**
 * Builds a LinkedIn share URL for a given public URL.
 */
export function buildLinkedInShareUrl(shareUrl: string): string {
    const params = new URLSearchParams({ url: shareUrl });
    return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
}

/**
 * Detects if the current environment supports mobile Web Share with files.
 * Used to decide whether to show the native share sheet.
 */
export function isMobileShareSupported(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const nav: any = navigator;
    const hasShare = typeof nav.share === 'function';
    const hasCanShare = typeof nav.canShare === 'function';
    const ua = navigator.userAgent || '';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return hasShare && hasCanShare && isMobile;
}

/**
 * Checks if the current browser environment can share files using the Web Share API.
 */
export function canShareFiles(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
        return false;
    }

    try {
        const nav: any = navigator;
        if (typeof nav.share !== 'function') return false;

        // If canShare is not available, assume basic share support (without strict file check)
        if (typeof nav.canShare !== 'function') {
            return true;
        }

        const testFile = new File([], 'x.pdf', { type: 'application/pdf' });
        return nav.canShare({ files: [testFile] });
    } catch {
        return false;
    }
}

export type FetchCertificatePdfArgs = {
    token: string;
    certIdOrSlug: string;
    locale: string;
    translatedTexts: Record<string, string>;
};

/**
 * Fetches the certificate PDF as a File for sharing via Web Share API.
 * Does NOT trigger a download – the caller is responsible for handling the File.
 */
export async function fetchCertificatePdfFile({
    token,
    certIdOrSlug,
    locale,
    translatedTexts,
}: FetchCertificatePdfArgs): Promise<File | null> {

    if (!token) {
        return null;
    }

    const params = new URLSearchParams({
        format: 'pdf',
        lang: locale,
        ...Object.fromEntries(
            Object.entries(translatedTexts).map(([key, value]) => [
                `text_${key}`,
                encodeURIComponent(value),
            ]),
        ),
    });

    const apiUrl = getApiUrl(`/dashboard/certificates/${certIdOrSlug}/download?${params.toString()}`);

    const res = await fetch(apiUrl, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    // Check auth errors (will redirect if needed)
    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        return null;
    }

    if (!res.ok) {
        throw new Error(`Failed to download certificate PDF: ${res.statusText}`);
    }

    const blob = await res.blob();
    const fileName = `cert_${certIdOrSlug}.pdf`;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    return file;
}

export type FetchCertificatePdfAsFileArgs = {
    token: string;
    certIdOrSlug: string;
    locale: string;
    translatedTexts: Record<string, string>;
};

/**
 * Fetches the certificate PDF from the existing download endpoint and returns it as a File.
 * Uses query parameters without manual double-encoding.
 */
export async function fetchCertificatePdfAsFile({
    token,
    certIdOrSlug,
    locale,
    translatedTexts,
}: FetchCertificatePdfAsFileArgs): Promise<File> {

    const params = new URLSearchParams({
        format: 'pdf',
        lang: locale,
    });

    Object.entries(translatedTexts || {}).forEach(([key, value]) => {
        params.set(`text_${key}`, value ?? '');
    });

    const apiUrl = getApiUrl(`/dashboard/certificates/${encodeURIComponent(certIdOrSlug)}/download?${params.toString()}`);

    const res = await fetch(apiUrl, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        throw new Error('Authentication error while downloading certificate PDF');
    }

    if (!res.ok) {
        throw new Error(`Failed to download certificate PDF: ${res.statusText}`);
    }

    const blob = await res.blob();
    const fileName = `cert_${certIdOrSlug}.pdf`;
    return new File([blob], fileName, { type: 'application/pdf' });
}

export type CertificateDownloadResponse = {
    url: string;
};

export async function getCertificates(
    token: string,
    lang: string = 'en',
): Promise<Certificate[]> {

    const res = await apiFetch(getApiUrl(`/dashboard/certificates?lang=${lang}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    // Check for authentication errors (403/Forbidden)
    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load certificates');
    }

    return (await res.json()) as Certificate[];
}

export async function getCertificate(
    token: string,
    certificateId: string,
): Promise<Certificate> {

    const res = await apiFetch(getApiUrl(`/dashboard/certificates/${certificateId}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load certificate');
    }

    return (await res.json()) as Certificate;
}

export async function downloadCertificate(
    token: string,
    certificateId: string,
): Promise<CertificateDownloadResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/certificates/${certificateId}/download`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to download certificate');
    }

    return (await res.json()) as CertificateDownloadResponse;
}

export type CertificateGenerationResponse = {
    id: string;
    fileUrl: string;
    issuedAt: string;
    shareSlug: string;
    shareUrl: string;
    linkedinShareUrl: string;
    course: {
        id: string;
        slug: string;
        level: string | number;
        title: string;
    };
};

export async function generateCertificate(
    token: string,
    courseSlug: string,
): Promise<CertificateGenerationResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/certificates/${courseSlug}/generate`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to generate certificate');
    }

    return (await res.json()) as CertificateGenerationResponse;
}

export type CertificateShareLinkResponse = {
    id: string;
    fileUrl: string;
    issuedAt: string;
    shareSlug: string;
    shareUrl: string;
    linkedinShareUrl: string;
    course: {
        id: string;
        slug: string;
        level: string | number;
        title: string;
    };
};

export async function getCertificateShareLink(
    token: string,
    certificateId: string,
): Promise<CertificateShareLinkResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/certificates/${certificateId}/share-link`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to get share link');
    }

    return (await res.json()) as CertificateShareLinkResponse;
}

/* ========= Profile ========= */

export type UserProfile = {
    id: string;
    email: string;
    fullName: string;
    role: string;
    phone?: string | null;
    alternateEmail?: string | null;
    universityId: string | null;
    universityDomain: string | null;
    profileImageUrl: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    createdAt: string;
    updatedAt: string;
    isSsoUser: boolean;
    passwordHash?: string; // Usually not returned, but included in type
};

export async function getProfile(
    token: string,
): Promise<UserProfile> {

    const res = await apiFetch(getApiUrl('/dashboard/profile'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    // Check for authentication errors (403/Forbidden)
    const authErrorHandled = await checkAuthError(res);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load profile');
    }

    return (await res.json()) as UserProfile;
}

export type ChangePasswordRequest = {
    currentPassword: string;
    newPassword: string;
};

export type ChangePasswordResponse = {
    success: boolean;
    message: string;
};

export async function changePassword(
    token: string,
    data: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {

    const res = await apiFetch(getApiUrl('/dashboard/profile/change-password'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to change password');
    }

    return (await res.json()) as ChangePasswordResponse;
}

export type UploadAvatarResponse = {
    url: string;
    profileImageUrl: string;
    success: boolean;
};

export async function uploadAvatar(
    token: string,
    file: File,
): Promise<UploadAvatarResponse> {

    const formData = new FormData();
    formData.append('file', file);

    const res = await apiFetch(getApiUrl('/dashboard/profile/avatar'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type header, browser will set it with boundary for FormData
        },
        body: formData,
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to upload avatar');
    }

    return (await res.json()) as UploadAvatarResponse;
}

export type UpdateProfileRequest = {
    fullName?: string;
    email?: string;
    phone?: string;
    profileImageUrl?: string | null;
    alternateEmail?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
};

// ========= Auth API Functions =========

export type SignupRequest = {
    email: string;
    password: string;
    fullName?: string;
    referralCode?: string;
    referralCoupon?: string | null;
    impactClickId?: string | null;
};

export type AuthUser = {
    id: string;
    email: string;
    fullName: string;
    emailVerified: boolean;
    role: string;
    alternateEmail?: string | null;
    universityName?: string | null;
    companyName?: string | null;
};

export type SignupResponse = {
    ok: boolean;
    authType: 'INDIVIDUAL' | 'SSO';
    needOtpVerification: boolean;
    existingUser?: boolean;
    message?: string;
};

// ========= Device Limit (max 3 devices) =========

export type DeviceSession = {
    id: string;
    deviceId: string;
    userAgent: string;
    ip?: string;
    createdAt: string;
    lastSeen: string;
    isCurrent: boolean;
};

export type DeviceLimitPayload = {
    ok: false;
    errorCode: 'DEVICE_LIMIT_REACHED';
    message: string;
    maxDevices: number;
    devices: DeviceSession[];
    /** Short-lived token for device removal when user has no auth (returned in 409 response) */
    removalToken?: string;
    token?: string;
};

export class DeviceLimitError extends Error {
    payload: DeviceLimitPayload;
    constructor(payload: DeviceLimitPayload) {
        super(payload.message);
        this.name = 'DeviceLimitError';
        this.payload = payload;
    }
}

/** For direct fetch calls: parse 409 response to get device limit payload. Uses clone to avoid consuming body. */
export async function parseDeviceLimitPayload(res: Response): Promise<DeviceLimitPayload | null> {
    if (res.status !== 409) return null;
    const body = await res.clone().json().catch(() => null);
    if (body?.errorCode === 'DEVICE_LIMIT_REACHED' && Array.isArray(body?.devices)) {
        return body as DeviceLimitPayload;
    }
    return null;
}

function getDeviceId(): string {
    if (typeof window === 'undefined') return 'web-client';
    const existing = localStorage.getItem('airies_device_id');
    if (existing) return existing;
    const generated =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? `web-client-${crypto.randomUUID()}`
            : `web-client-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('airies_device_id', generated);
    return generated;
}

export async function logout(token: string): Promise<{ ok: boolean }> {
    const deviceId = getDeviceId();
    const res = await fetch(getApiUrl('/auth/logout'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-device-id': deviceId,
        },
        credentials: 'include',
    });
    const data = await res.json().catch(() => ({ ok: false }));
    return { ok: data?.ok === true };
}

export async function signup(data: SignupRequest): Promise<SignupResponse> {
    const deviceId = getDeviceId();
    
    const res = await apiFetch(getApiUrl('/auth/individual/signup'), {
        method: 'POST',
        _skipAuth: true,
        headers: {
            'Content-Type': 'application/json',
            'x-device-id': deviceId,
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to sign up');
    }

    return (await res.json()) as SignupResponse;
}

export type LoginRequest = {
    email: string;
    password: string;
};

export type LoginResponse = {
    ok: boolean;
    authType: 'INDIVIDUAL' | 'SSO';
    token?: string;
    user?: AuthUser;
    needOtpVerification: boolean;
    message?: string;
};

export async function login(data: LoginRequest): Promise<LoginResponse> {
    const deviceId = getDeviceId();
    
    const res = await apiFetch(getApiUrl('/auth/individual/login'), {
        method: 'POST',
        _skipAuth: true,
        headers: {
            'Content-Type': 'application/json',
            'x-device-id': deviceId,
        },
        body: JSON.stringify({ ...data, licenseKey: getPendingLicenseKey() }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.errorCode === 'DEVICE_LIMIT_REACHED' && Array.isArray(body?.devices)) {
            throw new DeviceLimitError(body as DeviceLimitPayload);
        }
        throw new Error(body?.message || 'Failed to log in');
    }

    return (await res.json()) as LoginResponse;
}

export type VerifyOtpRequest = {
    email: string;
    otp: string;
    licenseKey?: string;
    impactClickId?: string | null;
};

export type VerifyOtpResponse = {
    ok: boolean;
    authType: 'INDIVIDUAL' | 'SSO';
    token: string;
    user: AuthUser;
    message?: string;
};

export async function verifySignupOtp(data: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    const deviceId = getDeviceId();
    
    const res = await apiFetch(getApiUrl('/auth/individual/verify-signup-otp'), {
        method: 'POST',
        _skipAuth: true,
        headers: {
            'Content-Type': 'application/json',
            'x-device-id': deviceId,
        },
        body: JSON.stringify({ ...data, licenseKey: data.licenseKey ?? getPendingLicenseKey() }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.errorCode === 'DEVICE_LIMIT_REACHED' && Array.isArray(body?.devices)) {
            throw new DeviceLimitError(body as DeviceLimitPayload);
        }
        throw new Error(body?.message || 'Failed to verify OTP');
    }

    return (await res.json()) as VerifyOtpResponse;
}

// ========= License API Functions =========

export type ValidateLicenseResponse = {
    valid: boolean;
    flow: 'INDIVIDUAL' | 'CORPORATE' | null;
    requiredAuth?: 'INDIVIDUAL' | 'SSO';
    reason?: string;
    seatsRemaining: number | null;
    cadence?: string | null;
    planId?: string | null;
};

export async function validateLicense(licenseKey: string, email: string): Promise<ValidateLicenseResponse> {
    const url = getApiUrl(`/licenses/validate?licenseKey=${encodeURIComponent(licenseKey)}&email=${encodeURIComponent(email)}`);
    const res = await apiFetch(url, { method: 'GET' });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to validate license');
    }
    return (await res.json()) as ValidateLicenseResponse;
}

export type ClaimLicenseResponse = {
    flow: 'INDIVIDUAL' | 'CORPORATE';
    nextPath: string;
};

export async function claimLicense(
    licenseKey: string,
    email: string,
    flow?: 'INDIVIDUAL' | 'CORPORATE',
    impactClickId?: string | null
): Promise<ClaimLicenseResponse> {
    const res = await apiFetch(getApiUrl('/licenses/claim'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            licenseKey,
            email,
            ...(flow && { flow }),
            ...(impactClickId && { impactClickId }),
        }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to claim license');
    }
    return (await res.json()) as ClaimLicenseResponse;
}

// ========= Password Reset API Functions =========

export async function sendOtpReset(email: string): Promise<{ ok: boolean }> {
    const res = await apiFetch(getApiUrl('/auth/send-otp-reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to send reset OTP');
    }
    return (await res.json()) as { ok: boolean };
}

export type VerifyResetOtpResponse = {
    ok: boolean;
    token: string;
    user: AuthUser;
};

export async function verifyResetOtp(
    email: string,
    otp: string,
    newPassword: string,
    licenseKey?: string | null
): Promise<VerifyResetOtpResponse> {
    const deviceId = getDeviceId();
    const res = await apiFetch(getApiUrl('/auth/verify-reset-otp'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-device-id': deviceId,
        },
        body: JSON.stringify({
            email,
            otp,
            newPassword,
            licenseKey: licenseKey ?? getPendingLicenseKey(),
        }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.errorCode === 'DEVICE_LIMIT_REACHED' && Array.isArray(body?.devices)) {
            throw new DeviceLimitError(body as DeviceLimitPayload);
        }
        throw new Error(body?.message || 'Failed to reset password');
    }
    return (await res.json()) as VerifyResetOtpResponse;
}

// Get current user info (includes authType)
export type MeResponse = {
    id: string;
    email: string;
    fullName: string;
    emailVerified: boolean;
    role: string;
    authType: 'INDIVIDUAL' | 'SSO';
    alternateEmail?: string | null;
    lastAuthMethod?: 'EMAIL' | 'GOOGLE' | 'SSO' | null;
    lastAuthAt?: string | null;
    universityName?: string | null;
    companyName?: string | null;
};

export async function getMe(token: string): Promise<MeResponse> {
    const deviceId = getDeviceId();
    const res = await apiFetch(getApiUrl('/auth/me'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-device-id': deviceId,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to get user info');
    }

    return (await res.json()) as MeResponse;
}

export type AuthMethodHintResponse = {
    ok: boolean;
    method: 'EMAIL' | 'GOOGLE' | 'SSO' | null;
};

export async function getAuthMethodHint(email: string): Promise<AuthMethodHintResponse> {
    const res = await apiFetch(getApiUrl('/auth/auth-method-hint'), {
        method: 'POST',
        _skipAuth: true,
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load auth method');
    }

    return (await res.json()) as AuthMethodHintResponse;
}

// ========= Device API (max 3 devices) =========

export type ListDevicesResponse = {
    devices: DeviceSession[];
};

export async function listDevices(token: string): Promise<ListDevicesResponse> {
    const deviceId = getDeviceId();
    const res = await apiFetch(getApiUrl('/auth/devices'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'x-device-id': deviceId,
        },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to list devices');
    }
    return (await res.json()) as ListDevicesResponse;
}

// ========= Tools Explained API =========

export type ToolExplainedItem = {
    id: string;
    slug: string;
    title: string;
    videoUrl: string;
    thumbnailUrl: string | null;
    playbackToken?: string; // optional, when token is not in videoUrl
    sortOrder: number;
};

export async function getToolExplainedList(
    token: string,
    lang: string,
    signal?: AbortSignal
): Promise<{ ok: true; items: ToolExplainedItem[] }> {
    const deviceId = getDeviceId();
    const url = getApiUrl(`/dashboard/tools-explained?lang=${encodeURIComponent(lang)}`);
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'x-device-id': deviceId,
        },
        signal,
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load tools explained');
    }

    const data = (await res.json()) as { ok?: boolean; items?: ToolExplainedItem[] };
    return {
        ok: true,
        items: data.items ?? [],
    };
}

export async function getToolExplainedItem(
    token: string,
    slug: string,
    lang: string
): Promise<{ ok: true; item: ToolExplainedItem }> {
    const deviceId = getDeviceId();
    const url = getApiUrl(
        `/dashboard/tools-explained/${encodeURIComponent(slug)}?lang=${encodeURIComponent(lang)}`
    );
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'x-device-id': deviceId,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 404) {
            throw new Error(body?.message || 'Tool not found');
        }
        throw new Error(body?.message || 'Failed to load tool');
    }

    const data = (await res.json()) as { ok?: boolean; item?: ToolExplainedItem };
    if (!data.item) {
        throw new Error('Tool not found');
    }
    return {
        ok: true,
        item: data.item,
    };
}

/** Remove a device session. Requires: removalToken from 409 payload, or Bearer token. At 409, backend should return removalToken for unauthenticated removal. */
export async function removeDevice(sessionId: string, token?: string | null): Promise<void> {
    const authToken = token ?? getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    const res = await apiFetch(getApiUrl(`/auth/devices/${encodeURIComponent(sessionId)}`), {
        method: 'DELETE',
        headers,
        _skipAuth: true,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to remove device');
    }
}

export type ClearAllDevicesResponse = { ok: true; cleared: number };

export async function clearAllDevices(token?: string | null): Promise<ClearAllDevicesResponse> {
    const authToken = token ?? getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    const res = await apiFetch(getApiUrl('/auth/devices/clear'), {
        method: 'POST',
        headers,
        _skipAuth: true,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to clear devices');
    }
    const body = await res.json().catch(() => null);
    return {
        ok: true,
        cleared: typeof body?.cleared === 'number' ? body.cleared : 0,
    };
}

// ========= Referral API Functions =========
/*
 * REFERRAL API - cURL EXAMPLES
 * Base: https://api.YOUR_DOMAIN.com/api
 *
 * 1. Validate referral:
 *    curl -s "https://api.YOUR_DOMAIN.com/api/referrals/validate?code=ABC12345"
 *    Expected: { "ok": true, "valid": true, "code": "ABC12345", "referrer": {...}, "affiliate": {...}, "message": "..." }
 *
 * 2. Individual signup:
 *    curl -X POST "https://api.YOUR_DOMAIN.com/api/auth/individual/signup" \
 *      -H "Content-Type: application/json" \
 *      -d '{"email":"a@b.com","password":"Test1234!","fullName":"A","referralCode":"ABC12345","referralCoupon":"TCR-1CW-NBJ"}'
 *    Expected: { "ok": true, "needOtpVerification": true, "existingUser": false, "authType": "INDIVIDUAL" }
 *
 * 3. OAuth exchange:
 *    curl -X POST "https://api.YOUR_DOMAIN.com/api/auth/oauth/supabase" \
 *      -H "Content-Type: application/json" \
 *      -H "Authorization: Bearer SUPABASE_ACCESS_TOKEN" \
 *      -d '{"referralCode":"ABC12345","referralCoupon":"TCR-1CW-NBJ","licenseKey":"LIC-XXXX"}'
 *
 * 4. My referral info:
 *    curl -s "https://api.YOUR_DOMAIN.com/api/referrals/me" -H "Authorization: Bearer APP_JWT_TOKEN"
 *
 * 5. Paid referrals list:
 *    curl -s "https://api.YOUR_DOMAIN.com/api/referrals/purchases?page=1&pageSize=20" \
 *      -H "Authorization: Bearer APP_JWT_TOKEN"
 *    Expected: { "ok": true, "items": [{ "referredUser": {...}, "purchasedAt": "...", "planKey": "pro", ... }] }
 */

export type ValidateReferralRequest = {
    code: string;
};

export type ValidateReferralResponse = {
    ok: boolean;
    valid: boolean;
    code?: string;
    referrer?: { id: string; fullName: string | null };
    affiliate?: {
        isAffiliate: boolean;
        isActive: boolean;
        couponCode: string | null;
    };
    message?: string;
};

export async function validateReferralCode(data: ValidateReferralRequest): Promise<ValidateReferralResponse> {
    // Get device ID
    const deviceId = getDeviceId();
    
    // Build URL with query parameter
    const baseUrl = getApiUrl('/referrals/validate');
    const url = `${baseUrl}?code=${encodeURIComponent(data.code)}`;
    
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'x-device-id': deviceId,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to validate referral code');
    }

    const response = (await res.json()) as ValidateReferralResponse;

    return {
        ok: response.ok ?? true,
        valid: response.valid ?? false,
        code: response.code,
        referrer: response.referrer,
        affiliate: response.affiliate,
        message: response.message,
    };
}

export type ReferralInfoResponse = {
    code: string;
    stats: {
        referredSignups: number;
        qualifiedPaid: number;
        freeMonthsEarned: number;
        freeMonthsApplied: number;
    };
};

export type ReferralInfo = {
    referralCode: string;
    referralLink: string;
    successfulReferralsCount: number;
    requiredReferrals: number;
    rewardEarned: boolean;
    referredSignups: number;
    qualifiedPaid: number;
    freeMonthsEarned: number;
    freeMonthsApplied: number;
};

export async function getReferralInfo(token: string): Promise<ReferralInfo> {

    const res = await apiFetch(getApiUrl('/referrals/me'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load referral info');
    }

    const data = (await res.json()) as ReferralInfoResponse;
    
    // Map API response to our ReferralInfo type
    // Using referredSignups as successful referrals count
    // Assuming 2 referrals required for reward (can be adjusted)
    const requiredReferrals = 2;
    const referralCode = data.code || '';
    const successfulReferralsCount = data.stats?.referredSignups || 0;
    const rewardEarned = (data.stats?.freeMonthsEarned || 0) > 0 || successfulReferralsCount >= requiredReferrals;
    
    // Build referral link
    const referralLink = typeof window !== 'undefined' && referralCode
        ? `${window.location.origin}/pricing?ref=${encodeURIComponent(referralCode)}`
        : '';

    return {
        referralCode,
        referralLink,
        successfulReferralsCount,
        requiredReferrals,
        rewardEarned,
        referredSignups: data.stats?.referredSignups || 0,
        qualifiedPaid: data.stats?.qualifiedPaid || 0,
        freeMonthsEarned: data.stats?.freeMonthsEarned || 0,
        freeMonthsApplied: data.stats?.freeMonthsApplied || 0,
    };
}

export type ReferralPurchaseItem = {
    referredUser: {
        id: string;
        fullName: string | null;
        email?: string | null;
    };
    purchasedAt: string;
    planKey: string | null;
    planName: string | null;
    cbOrderId: string | null;
    cbSubscriptionId: string | null;
    couponCode: string | null;
};

export type ReferralPurchasesResponse = {
    ok: boolean;
    items: ReferralPurchaseItem[];
    meta?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export async function getReferralPurchases(
    token: string,
    page = 1,
    pageSize = 20
): Promise<ReferralPurchasesResponse> {
    const url = getApiUrl(`/referrals/purchases?page=${page}&pageSize=${pageSize}`);
    const res = await apiFetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load referral purchases');
    }

    return (await res.json()) as ReferralPurchasesResponse;
}

export async function updateProfile(
    token: string,
    data: UpdateProfileRequest,
): Promise<UserProfile> {

    const res = await apiFetch(getApiUrl('/dashboard/profile'), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to update profile');
    }

    return (await res.json()) as UserProfile;
}

// ========= Subscription API Functions =========

export type SubscriptionPlan = {
    id: string;
    slug: string;
    tier: string | null;
    code: string | null;
    currency: string;
    priceMonthlyCents: number | null;
    priceYearlyCents: number | null;
    showPrice: boolean;
    ctaType: 'LOGIN' | 'COMING SOON' | 'CONTACT';
    ctaLabel: string | null;
    ctaUrl: string | null;
    name: string;
    subtitle: string;
    sortOrder: number | null;
    isActive: boolean;
    features?: Array<{
        id: string;
        code: string;
        label: string;
        included: boolean;
        value: string | null;
    }>;
    // Legacy fields for backward compatibility
    amountCents?: number;
    interval?: string;
};

export type CurrentSubscription = {
    subscriptionId: string;
    planId: string;
    slug: string;
    tier: string | null;
    code: string | null;
    currency: string;
    priceMonthlyCents: number | null;
    priceYearlyCents: number | null;
    showPrice: boolean;
    name: string;
    subtitle: string;
    status?: string | null;
    startedAt?: string | null;
    endedAt?: string | null;
    endsAt?: string | null;
    providerSubId?: string | null;
    manageUrl?: string | null;
    cancelUrl?: string | null;
    changePaymentUrl?: string | null;
    expiringSoon?: boolean | null;
    daysUntilExpiry?: number | null;
    isPaid?: boolean | null;
    isFree?: boolean | null;
    planSlug?: string | null;
    planName?: string | null;
    providerSubscriptionId?: string | null;
    licenseKey?: string | null;
    seatsPurchased?: number | null;
    seatsUsed?: number | null;
    isAdmin?: boolean | null;
    flow?: 'INDIVIDUAL' | 'CORPORATE' | null;
    provider?: 'STRIPE' | 'CLEVERBRIDGE' | null;
    billingPortalEligible?: boolean | null;
    source: string;
    // Legacy fields for backward compatibility
    amountCents?: number;
    interval?: string;
};

export type DashboardCourseEntitlement = {
    locked?: boolean | null;
    lockType?: string | null;
    reason?: string | null;
    progress?: number | null;
    progressPercent?: number | null;
    completed?: boolean | null;
    status?: string | null;
};

export type DashboardCourseSummary = DashboardCourseEntitlement & {
    id: string;
    slug: string;
    title: string;
    level?: number | null;
    category?: string | null;
    thumbnailUrl?: string | null;
    totalLessons?: number | null;
    completedLessons?: number | null;
};

export type SubscriptionResponse = {
    currentPlan: CurrentSubscription | null;
    subscription?: CurrentSubscription | null;
    legacyMigration?: {
        eligible: boolean;
        showPrompt: boolean;
        flow: 'INDIVIDUAL' | 'CORPORATE';
        planId: string;
        paidThroughAt: string;
        currency: string;
        amountCents: number;
        cadence: 'month' | 'year';
    } | null;
    expiredPaidPlan?: {
        showWarning: boolean;
        dismissed: boolean;
        planName: string | null;
        planCode: string | null;
        planSlug: string | null;
        endedAt: string | null;
    };
    availablePlans: SubscriptionPlan[];
    actions: {
        canUpgrade: boolean;
        canDowngrade: boolean;
    };
};

export async function getSubscription(token: string, lang: string = 'en'): Promise<SubscriptionResponse> {

    const res = await apiFetch(getApiUrl(`/dashboard/profile/subscription?lang=${encodeURIComponent(lang)}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load subscription');
    }

    return (await res.json()) as SubscriptionResponse;
}

export async function dismissLegacyMigration(token: string): Promise<void> {
    const res = await apiFetch(getApiUrl('/dashboard/profile/subscription/legacy-migration/dismiss'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Could not dismiss the reminder');
}

export async function dismissExpiredPlanWarning(token: string): Promise<{ ok: boolean }> {
    const res = await apiFetch(getApiUrl('/dashboard/profile/subscription/expired-plan-warning/dismiss'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to dismiss plan warning');
    }

    return (await res.json()) as { ok: boolean };
}

export type StripeCheckoutSyncResponse = {
    ok: boolean;
    sessionId: string;
    status: string | null;
    paymentStatus: string | null;
};

export async function syncStripeCheckoutSession(
    token: string,
    sessionId: string,
): Promise<StripeCheckoutSyncResponse> {
    const normalizedSessionId = (sessionId || '').trim();
    if (!normalizedSessionId) throw new Error('sessionId is required');

    const res = await apiFetch(getApiUrl('/stripe/sync-checkout-session'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ sessionId: normalizedSessionId }),
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to sync checkout session');
    }

    return (await res.json()) as StripeCheckoutSyncResponse;
}

// ========= Billing API Functions =========

export type BillingItem = {
    id: string;
    invoiceNumber?: string;
    invoiceNo?: string;
    date: string;
    planName: string;
    amount?: string;
    amountCents?: number;
    currency: string;
    status: string;
    downloadUrl?: string | null;
    invoiceUrl?: string | null;
};

export type BillingResponse = {
    items: BillingItem[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
};

export async function getBilling(
    token: string,
    page: number = 1,
    pageSize: number = 20,
    lang: string = 'en'
): Promise<BillingResponse> {

    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        lang: lang,
    });

    const res = await apiFetch(getApiUrl(`/dashboard/profile/billing?${params.toString()}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    const authErrorHandled = await checkAuthError(res, undefined);
    if (authErrorHandled) {
        throw new Error('Session expired');
    }

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Failed to load billing history');
    }

    return (await res.json()) as BillingResponse;
}

// ========= OAuth API Functions =========

export type OAuthSupabaseRequest = {
    referralCode?: string | null;
    referralCoupon?: string | null;
    impactClickId?: string | null;
};

export type OAuthSupabaseResponse = {
    ok: boolean;
    authType: 'OAUTH';
    token: string;
    user: {
        id: string;
        email: string;
        fullName: string;
        emailVerified: boolean;
        role: string;
    };
};

export async function exchangeSupabaseToken(
    supabaseAccessToken: string,
    referralCode?: string | null,
    referralCoupon?: string | null,
    licenseKey?: string | null,
    impactClickId?: string | null
): Promise<OAuthSupabaseResponse> {

    const deviceId = getDeviceId();
    const res = await apiFetch(getApiUrl('/auth/oauth/supabase'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAccessToken}`,
            'x-device-id': deviceId,
        },
        body: JSON.stringify({
            referralCode: referralCode || null,
            referralCoupon: referralCoupon || null,
            ...(licenseKey && { licenseKey }),
            ...(impactClickId && { impactClickId }),
        } as OAuthSupabaseRequest & { licenseKey?: string }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.errorCode === 'DEVICE_LIMIT_REACHED' && Array.isArray(body?.devices)) {
            throw new DeviceLimitError(body as DeviceLimitPayload);
        }
        throw new Error(body?.message || 'Failed to exchange OAuth token');
    }

    return (await res.json()) as OAuthSupabaseResponse;
}
